<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { fetchGetOrgUnitsByUserIdTree, fetchGetUserResponsibilities } from '@/service/api';
import { useAuthStore } from '@/store/modules/auth';
import { useOrgStore } from '@/store/modules/org';

interface Props {
  show: boolean;
  loading?: boolean;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (
    e: 'confirm',
    orgId: string,
    orgName: string,
    orgCode: string,
    deptId?: string,
    deptName?: string,
    deptCode?: string,
    bestSignAccount?: string,
    unifiedCode?: string
  ): void;
  (e: 'cancel'): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});
const emit = defineEmits<Emits>();

// 获取当前用户信息和组织信息
const authStore = useAuthStore();
const orgStore = useOrgStore();

// 组织选择相关
const selectedOrg = ref<null | string>(null);
const selectedDept = ref<null | string>(null);

const loadingOrgs = ref(false);

// 组织和部门数据
const organizations = ref<Api.SystemManage.OrgUnitsTree[]>([]);

// 获取组织列表
async function loadOrganizations() {
  try {
    loadingOrgs.value = true;
    const { data } = await fetchGetOrgUnitsByUserIdTree();
    organizations.value = data || [];
  } catch (error: any) {
    window.$message?.error(error?.message || '获取组织列表失败');
  } finally {
    loadingOrgs.value = false;
  }
}
const orgOptions = computed(() => {
  return organizations.value.map(org => ({
    label: org.name,
    value: org.id,
    code: org.code // 可选：保留 code 供后续使用
  }));
});

// 部门选项
const deptOptions = computed(() => {
  if (!selectedOrg.value) return [];

  const selectedOrgData = organizations.value.find(org => org.id === selectedOrg.value);
  if (!selectedOrgData || !selectedOrgData.children) return [];

  return selectedOrgData.children.map(dept => ({
    label: dept.name,
    value: dept.id,
    code: dept.code // 可选：保留 code 供后续使用
  }));
});

// 处理组织变化
function handleOrgChange(value: string) {
  selectedOrg.value = value;
  selectedDept.value = ''; // 清空部门选择
}

// 监听弹窗显示状态
watch(
  () => props.show,
  async newShow => {
    if (newShow) {
      await loadOrganizations();

      if (organizations.value.length > 0) {
        const match = organizations.value.find(org => org.code === orgStore.currentOrgInfo?.code);
        selectedOrg.value = match ? match.id : organizations.value[0].id;

        if (Array.isArray(match?.children)) {
          const dep = match?.children.find(org => org.code === orgStore.currentDeptInfo?.code);
          selectedDept.value = dep ? dep.id : null;
        }
      }
    } else {
      selectedOrg.value = '';
      selectedDept.value = '';
    }
  }
);

// 确认选择
async function handleConfirm() {
  const selectedOrgData = organizations.value.find(org => org.id === selectedOrg.value);
  console.log('selectedOrgData', selectedOrgData);

  if (!selectedOrgData) return;

  let selectedDeptData = null;
  if (selectedDept.value && selectedOrgData.children) {
    selectedDeptData = selectedOrgData.children.find(dept => dept.id === selectedDept.value);
  }

  // 回传组织和部门信息
  emit(
    'confirm',
    selectedOrgData.id,
    selectedOrgData.name,
    selectedOrgData.code,

    selectedDeptData?.id,
    selectedDeptData?.name,
    selectedDeptData?.code,
    selectedOrgData.bestSignAccount,
    // 组织（父节点）的统一社会信用代码，选择组织或部门时均取组织层级
    selectedOrgData.unifiedCode
  );
}

// 取消选择
function handleCancel() {
  emit('cancel');
}
</script>

<template>
  <NModal
    :show="show"
    preset="card"
    title="请选择组织和部门"
    style="width: 500px; max-width: 90vw"
    :mask-closable="false"
    :closable="false"
    class="org-selector-modal"
    @update:show="value => emit('update:show', value)"
  >
    <div class="org-selection">
      <NForm :model="{ org: selectedOrg, dept: selectedDept }" label-placement="left" label-width="80" class="org-form">
        <NFormItem label="组织" class="form-item-center">
          <NSelect
            v-model:value="selectedOrg"
            :options="orgOptions"
            placeholder="请选择组织"
            :loading="loadingOrgs"
            filterable
            clearable
            class="select-center"
            @update:value="handleOrgChange"
          />
        </NFormItem>
        <NFormItem label="业务单位" class="form-item-center">
          <NSelect
            v-model:value="selectedDept"
            :options="deptOptions"
            placeholder="请选择业务单位"
            :disabled="!selectedOrg"
            filterable
            clearable
            class="select-center"
          />
        </NFormItem>
      </NForm>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <NButton @click="handleCancel">取消</NButton>
        <NButton type="primary" :loading="props.loading" @click="handleConfirm">确认</NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.org-selection {
  padding: 16px 0;
}

.org-selection :deep(.n-form-item) {
  margin-bottom: 24px;
}

.org-selection :deep(.n-select) {
  width: 100%;
}

/* 添加加载状态样式 */
.org-selection :deep(.n-select .n-base-selection) {
  min-height: 36px;
}

/* 表单居中样式 */
.org-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.form-item-center {
  width: 100%;
  max-width: 400px;
  text-align: center;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.form-item-center :deep(.n-form-item-label) {
  text-align: center;
  justify-content: center;
  font-weight: 500;
  width: 80px;
  flex-shrink: 0;
}

.form-item-center :deep(.n-form-item-blank) {
  display: flex;
  justify-content: center;
  flex: 1;
  max-width: 300px;
}

.select-center {
  width: 100%;
  max-width: 300px;
}

.select-center :deep(.n-base-selection) {
  margin: 0 auto;
  width: 100%;
}
</style>

<style>
/* 全局样式，确保弹窗居中 */
.org-selector-modal .n-modal-body-wrapper {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 100vh !important;
  transform-style: preserve-3d !important;
}

.org-selector-modal .n-card {
  margin: 0 !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
  border-radius: 12px !important;
  max-height: 90vh !important;
  overflow-y: auto !important;
  transform: translateZ(0) !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .org-selector-modal .n-card {
    width: 95vw !important;
    max-width: 95vw !important;
    margin: 10px !important;
  }
}
</style>
